import { useCourseStateContext } from '@components/context/CourseContext';
import { CourseTerm, CourseTermArguments } from '@graphql/generated-graphql';
import React, { useCallback, useMemo, useState } from 'react';
import create, { StateCreator } from 'zustand';

enum CourseTerms {
    Semester = 'Semester',
    Trimester = 'Trimester',
    Quarter = 'Quarter',
    Term = 'Term',
}

export function useCourseTerms() {
    const [courseTerms] = useState(() => [
        { count: 2, term: CourseTerms.Semester },
        { count: 3, term: CourseTerms.Trimester },
        { count: 4, term: CourseTerms.Quarter },
        { count: 6, term: CourseTerms.Term },
    ]);

    return courseTerms;
}

enum TermDropdownState {
    SHOW_TERMS = 'SHOW_TERMS',
    UNITIALIZED = 'UNITIALIZED',
}

interface CourseTermsSlice {
    courseId: string | null;
    courseTerms: CourseTerm[] | null | undefined;
    currentTerm: CourseTerm | null | undefined;
    selectedTerm: CourseTerm | null | undefined;
    setTerms: (terms: CourseTerm[]) => void;
    setCurrentTerm: (term: CourseTerm) => void;
    setSelectedTerm: (term: CourseTerm) => void;
    initializeTerm: (courseId: string, term: CourseTerm, termsList: CourseTerm[]) => void;
}

const createCourseTermSlice: StateCreator<CourseTermsSlice, [], [], CourseTermsSlice> = (
    set,
    get,
) => ({
    courseId: null,
    courseTerms: null,
    currentTerm: null,
    selectedTerm: null,
    setTerms: (terms: CourseTerm[]) => set(() => ({ courseTerms: terms })),
    setCurrentTerm: (term: CourseTerm) => set(() => ({ currentTerm: term })),
    setSelectedTerm: (term: CourseTerm) => set(() => ({ selectedTerm: term })),
    initializeTerm: (courseId: string, term: CourseTerm | null, termsList: CourseTerm[]) =>
        set(() => {
            const prevCurrentTerm = get().currentTerm;
            const selectedTerm = get().selectedTerm;
            const previousCourseId = get().courseId;
            if ((selectedTerm === null && !!term) || previousCourseId !== courseId || prevCurrentTerm?.id !== term?.id) {
                return {
                    courseId,
                    selectedTerm: term,
                    currentTerm: term,
                    courseTerms: termsList,
                };
            }
            return {
                courseId,
                currentTerm: term,
                courseTerms: termsList,
            };
        }),
});

export const useCourseTermsStore = create<CourseTermsSlice>()((...a) => ({
    ...createCourseTermSlice(...a),
}));

export function useCourseTermsHandler() {
    return useCourseTermsStore((state) => ({
        showTerms: !!state.currentTerm && (state.courseTerms.filter(t => t.published).length > 1 || state.selectedTerm?.id !== state.currentTerm?.id),
        currentTerm: state.currentTerm,
        selectedTerm: state.selectedTerm,
        currentSelectedTermId: state.selectedTerm?.id,
        setSelectedTerm: state.setSelectedTerm,
        termList: state.courseTerms,
    }));
}

/**
 * Just get default terms query as it should of already been requested by this point.
 *
 * @returns
 */
export function useCourseTermDropdown() {
    const { data } = useCourseStateContext();

    return useMemo(
        () => ({
            currentTerm: data?.currentTerm ?? null,
            usingTerms: data?.currentTerm !== null,
            terms: data?.terms ?? [],
        }),
        [data],
    );
}
